import React, { useMemo, useEffect, type ChangeEvent, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { RadioGroup } from '@atlaskit/radio';
import { Text, Stack, xcss } from '@atlaskit/primitives';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { ENTRYPOINT_ID_JIRA_PLATFORM } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import messages from './messages.tsx';
import {
	YES_PLEASE_MIGRATE_VALUE,
	NO_DO_NOT_MIGRATE_VALUE,
	FEEDBACK_GROUP_ID,
	STARGATE_URL,
	ENV_STAGING,
	MIGRATION_FEEDBACK_MSG_ENGAGEMENT_ID_STG,
	MIGRATION_FEEDBACK_MSG_ENGAGEMENT_ID_PROD,
} from './constants.tsx';
import { buildAdditionalFields } from './utils.tsx';

export const MigrationFeedback = () => {
	const radioGroupId = `radio-group-${uuid()}`;
	const { formatMessage } = useIntl();

	const { updateAdditionalFields } = useFeedbackCollectorActions();

	const onChangeMigrationOption = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			updateAdditionalFields(buildAdditionalFields(event.target.value));
		},
		[updateAdditionalFields],
	);

	const MSG_ENGAGEMENT_ID =
		useEnvironment() === ENV_STAGING
			? MIGRATION_FEEDBACK_MSG_ENGAGEMENT_ID_STG
			: MIGRATION_FEEDBACK_MSG_ENGAGEMENT_ID_PROD;
	const cloudId = useCloudId();

	const coordinationClient = useMemo(
		// eslint-disable-next-line jira/choreographer/choreographer-context-required
		() => new CoordinationClient(cloudId, STARGATE_URL, false, '', 'engagement'),
		[cloudId],
	);

	const [isEnrolled, stopEnrolment] = useCoordination(coordinationClient, MSG_ENGAGEMENT_ID);

	const { openFeedbackCollector } = useFeedbackCollectorActions();

	useEffect(() => {
		if (isEnrolled) {
			stopEnrolment(false);
			openFeedbackCollector({
				showTypeField: false,
				feedbackTitle: formatMessage(messages.feedbackTitle),
				feedbackTitleDetails: formatMessage(messages.feedbackTitleDetails),
				entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
				feedbackGroupId: FEEDBACK_GROUP_ID,
				submitButtonLabel: formatMessage(messages.feedbackSubmitButton),
				// @ts-expect-error: Object literal may only specify known properties, and 'customTextAreaLabel' does not exist in type 'FeedbackCollectorProps'.ts(2345)
				customTextAreaLabel: formatMessage(messages.feedbackTextareaLabel),
				renderCustomContent: () => (
					<Stack space="space.100" xcss={wrapperStyle}>
						<Text id={radioGroupId}>{formatMessage(messages.feedbackOptionsLabel)}</Text>
						<RadioGroup
							testId="business-list.ui.list-view.list-migration-feedback.radio-group"
							options={[
								{ value: YES_PLEASE_MIGRATE_VALUE, label: formatMessage(messages.migrateSettings) },
								{
									value: NO_DO_NOT_MIGRATE_VALUE,
									label: formatMessage(messages.noMigrateSettings),
								},
							]}
							onChange={onChangeMigrationOption}
							aria-labelledby={radioGroupId}
						/>
					</Stack>
				),
				additionalFields: buildAdditionalFields(),
			});
		}
	}, [
		formatMessage,
		isEnrolled,
		onChangeMigrationOption,
		openFeedbackCollector,
		radioGroupId,
		stopEnrolment,
	]);

	return null;
};

const wrapperStyle = xcss({
	marginTop: 'space.200',
});
